<template>
	<div>

		<p class="step-title">
			{{ $t('account/bookings.Documents') }}
		</p>

		<booking-alert
			class="mt-3"
			v-if="!complete"
		>
			{{ $t('account/bookings.Documents are required') }}
		</booking-alert>

		<transition-group name="slide-fade">
			<div
				:key="'error-' + error.id"
				v-for="(error, i) in errors"
				:class="i == 0 ? 'mt-4' : ''"
				class="alert bg-danger small text-white mb-2"
			>
				<i class="fe fe-file mr-2"></i> <strong>{{ error.name }}</strong> :
				<div class="fs-small mt-2">
					{{ $t('account/bookings.Document error ' + error.type, {
						extension: error.extension,
						size: $getSize(error.size),
						maxSize: maxSize,
						extensions: extensions.join(', ')
					}) }}
				</div>
			</div>
		</transition-group>

		<transition name="slide-down">

			<div
				v-if="documents.length"
				class="card card-services bg-lighten shadow-none col-md-12 mt-4"
			>

				<div class="card-header">
					<p class="h5 text-center">
						{{ $t('account/bookings.Your added documents') }}
					</p>
				</div>

				<div class="card-body">

					<transition-group
						tag="div"
						class="list-group list-group-flush"
						name="slide-fade"
					>

						<div
							v-for="(item, i) in documents"
							:key="'file-' + i"
							class="list-group-item d-flex justify-content-between align-items-center bg-transparent"
						>

							<span>
								<i class="fe fe-file mr-3"></i>
								{{ item.name }}

								<small class="ml-4 text-muted">
									{{ $getSize(item.file.size) }}
								</small>

							</span>

							<div>

								<button
									@click="editFile(i, item)"
									class="btn btn-neutral py-2 btn-xs mr-1"
								>
									<i class="fe fe-edit-2"></i>
								</button>

								<button
									@click="removeFile(i)"
									class="btn btn-danger py-2 btn-xs"
								>
									<i class="fe fe-trash-2"></i>
								</button>

							</div>

						</div> <!-- /.list-group-item -->

					</transition-group>

				</div> <!-- /.card-body -->

			</div> <!-- /.card -->

		</transition>

		<div
			class="dropzone-container mt-4 mb-3"
			:class="{
				'is-dragging': isDragging
			}"
			@dragover="dragover"
			@dragleave="dragleave"
			@drop="drop"
		>

			<p class="text-center pt-3 pb-3">
				<i class="bi bi-files mr-3"></i>
				{{ $t('account/bookings.Drag and drop documents') }}
			</p>

			<div class="text-center">

				<button
					:disabled="isDragging"
					@click="$refs.files.click()"
					class="btn btn-primary mb-3"
				>
					<i class="bi bi-files mr-3"></i>
					{{ $t('account/bookings.Add my documents') }}
				</button>

				<input
					class="d-none"
					@input="drop"
					ref="files"
					type="file"
					multiple
				>

			</div>

		</div> <!-- /.dropzone-container -->

		<modal
			size="md"
			:title="$t('account/bookings.Edit document')"
			:button="$t('app.Update')"
			@submit="updateDocument"
			:disabled="!document.name"
			ref="modal"
		>

			<input-select
				:name="$t('account/bookings.Document type')"
				:clearable="false"
				:options="booking.documentTypes"
				:reduce="o => o.value"
				v-model="document.type"
			/>

			<input-text
				:label="$t('account/bookings.Document name')"
				v-model="document.name"
			/>

		</modal>

	</div>
</template>

<script>

import { mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid';

export default {

	data() {
		return {
			isDragging: false,
			index: null,
			document: {},
			complete: true,
			errors: [],
			files: [],
			maxSize: 15,
			extensions: ['jpg', 'jpeg', 'png', 'pdf', 'csv', 'docx', 'xls', 'xlsx']
		}
	},

	computed: {

		...mapState({
			booking: state => state.booking,
		}),

		documents: {
			get() {
				return this.booking.documents
			},
			set(value) {
				this.$store.commit('booking/setDocuments', value)
			}
		}

	},

	methods: {

		checkComplete() {
			const { service, services } = this.booking;

			if (!this.documents.length && service === 'parking' && ['simple', 'multiple'].includes(services.cts_service)) {
				return this.complete = true;
			}

			return this.complete = !!this.documents.length;
		},

		dragover(e) {
			e.preventDefault();
			this.isDragging = true;
		},

		dragleave() {
			this.isDragging = false;
		},

		drop(e) {
			e.preventDefault();
			this.isDragging = false;

			var value = e.target.files || e.dataTransfer.files;

			var enhancedFiles = Array.from(value).map(file => ({
				file: file,
				type: 'document',
				name: file.name.split('.')[0]
			}));

			this.errors = [];

			enhancedFiles = _.filter(enhancedFiles, item => {

				var extension = item.file.name.split('.').pop().toLowerCase()

				var error = {
					id: uuidv4(),
					name: item.file.name,
					size: item.file.size,
					extension: extension,
				}

				if (!this.extensions.includes(extension)) {
					error.type = 'extension'
					this.errors.push(error)
					return false;
				}
				else if (item.file.size > this.maxSize * 1000000) {
					error.type = 'size'
					this.errors.push(error)
					return false;
				}

				return true;

			})

			this.documents = [...this.documents, ...enhancedFiles];
			this.checkComplete()
		},

		editFile(index, file) {
			this.index = index
			this.document = _.cloneDeep(file)
			this.$refs.modal.open()
		},

		updateDocument() {

			var documents = _.cloneDeep(this.documents)
			documents[this.index] = this.document
			this.documents = documents

			this.$refs.modal.close()
		},

		removeFile(i) {
			this.documents.splice(i, 1)
		}
	},

}
</script>